<template>
  <article class="document-page">
    <header class="document-header">
      <p class="protocol-label">WebSocket channel</p>
      <div class="title-row">
        <h1>{{ title }}</h1>
        <span v-if="introducedIn" class="release-badge">Introduced in v{{ introducedIn }}</span>
      </div>
      <p v-if="shortDescription">{{ shortDescription }}</p>
      <SafeHtml v-if="description" :content="description" />
    </header>

    <section v-if="endpoint" class="document-section">
      <h2>Connect</h2>
      <p class="muted-text">Create a WebSocket connection using this URL.</p>
      <div class="endpoint-panel websocket-panel">
        <span class="method-pill ws">WS</span>
        <code>{{ endpoint }}</code>
        <CopyButton :value="endpoint" label="WebSocket URL" />
      </div>
    </section>

    <section v-if="endpoint" class="document-section">
      <div class="section-heading">
        <div>
          <h2>Example WebSocket client</h2>
          <p class="sample-caption">{{ activeExampleMeta.caption }}</p>
        </div>
        <CopyButton :value="activeExample" :label="`${activeExampleMeta.label} example`" />
      </div>
      <div class="example-tabs" role="tablist" aria-label="Example language">
        <button
          v-for="example in examples"
          :key="example.id"
          class="example-tab"
          :class="{ active: selectedExample === example.id }"
          type="button"
          role="tab"
          :aria-selected="selectedExample === example.id"
          @click="selectExample(example.id)"
        >
          {{ example.label }}
        </button>
      </div>
      <pre class="code-panel"><code>{{ activeExample }}</code></pre>
    </section>

    <div class="lifecycle-grid">
      <section v-if="onOpenDesc" class="document-section lifecycle-card">
        <p class="step-label">1. On open</p>
        <h2>Send a start message</h2>
        <SafeHtml :content="onOpenDesc" />
      </section>

      <section v-if="returns" class="document-section lifecycle-card">
        <div class="section-heading">
          <div>
            <p class="step-label">2. On message</p>
            <h2>Receive data</h2>
          </div>
          <CopyButton :value="formattedReturns" label="message payload" />
        </div>
        <pre class="code-panel"><code>{{ formattedReturns }}</code></pre>
      </section>

      <section v-if="onCloseDesc" class="document-section lifecycle-card">
        <p class="step-label">3. Close</p>
        <h2>End the connection</h2>
        <SafeHtml :content="onCloseDesc" />
      </section>
    </div>
  </article>
</template>

<script>
import { computed, ref } from 'vue';
import CopyButton from './CopyButton.vue';
import SafeHtml from './SafeHtml.vue';

const EXAMPLE_LANGUAGE_STORAGE_KEY = 'apiDocsExampleLanguage';
const exampleLanguageOptions = ['javascript', 'python'];
const getStoredExampleLanguage = () => {
  const storedLanguage = window.localStorage.getItem(EXAMPLE_LANGUAGE_STORAGE_KEY);
  return exampleLanguageOptions.includes(storedLanguage) ? storedLanguage : 'javascript';
};

export default {
  name: 'Websocket',
  components: { CopyButton, SafeHtml },
  props: {
    title: { type: String, default: '' },
    shortDescription: { type: String, default: '' },
    description: { type: String, default: '' },
    endpoint: { type: String, default: '' },
    returns: { type: [String, Object, Array], default: '' },
    onOpenDesc: { type: String, default: '' },
    onCloseDesc: { type: String, default: '' },
    introducedIn: { type: String, default: '' }
  },
  setup(props) {
    const selectedExample = ref(getStoredExampleLanguage());
    const selectExample = (language) => {
      selectedExample.value = language;
      window.localStorage.setItem(EXAMPLE_LANGUAGE_STORAGE_KEY, language);
    };
    const makePlaceholder = (name) => `YOUR_${name.replace(/([a-z])([A-Z])/g, '$1_$2').toUpperCase()}`;
    const extractStartMessage = () => {
      const match = (props.onOpenDesc || '').match(/"([^"]+)"/);
      if (!match) return 'YOUR_START_MESSAGE';

      return match[1].replace(/:([A-Za-z0-9_]+)/g, (_, name) => makePlaceholder(name));
    };
    const formattedReturns = computed(() => (
      typeof props.returns === 'string' ? props.returns : JSON.stringify(props.returns, null, 2)
    ));
    const javascriptExample = computed(() => [
      `const socket = new WebSocket('${(props.endpoint || 'ws://[your-websocket-host]').trim()}');`,
      '',
      "socket.addEventListener('open', () => {",
      `  socket.send('${extractStartMessage()}');`,
      '});',
      '',
      "socket.addEventListener('message', async (event) => {",
      '  const text = event.data instanceof Blob',
      '    ? await event.data.text()',
      '    : event.data instanceof ArrayBuffer',
      '      ? new TextDecoder().decode(event.data)',
      '      : event.data;',
      '',
      '  const data = JSON.parse(text);',
      '  console.log(data);',
      '});',
      '',
      "socket.addEventListener('close', () => {",
      "  console.log('WebSocket closed');",
      '});',
      '',
      "socket.addEventListener('error', (error) => {",
      "  console.error('WebSocket error:', error);",
      '});'
    ].join('\n'));
    const pythonExample = computed(() => [
      'import asyncio',
      'import json',
      'import websockets',
      '',
      '',
      'async def main():',
      `    uri = '${(props.endpoint || 'ws://[your-websocket-host]').trim()}'`,
      '',
      '    async with websockets.connect(uri) as websocket:',
      `        await websocket.send('${extractStartMessage()}')`,
      '',
      '        message = await websocket.recv()',
      '        if isinstance(message, bytes):',
      "            message = message.decode('utf-8')",
      '',
      '        data = json.loads(message)',
      '        print(data)',
      '',
      '',
      'asyncio.run(main())'
    ].join('\n'));
    const examples = computed(() => [
      {
        id: 'javascript',
        label: 'JavaScript',
        caption: 'JavaScript using the browser WebSocket API',
        code: javascriptExample.value
      },
      {
        id: 'python',
        label: 'Python',
        caption: 'Python using websockets',
        code: pythonExample.value
      }
    ]);
    const activeExampleMeta = computed(() => examples.value.find((example) => example.id === selectedExample.value) || examples.value[0]);
    const activeExample = computed(() => activeExampleMeta.value.code);

    return { activeExample, activeExampleMeta, examples, formattedReturns, selectExample, selectedExample };
  }
};
</script>
